import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { Repeat } from 'lucide-react';
import { Button } from '@hqo/ui-atoms';
import { ActivityRating } from 'app/components/activity-rating';

interface CardRatingFooterProps {
  rating: number;
  /** False when the item has no feedback outstanding — the footer then carries only "Book again". */
  showRating?: boolean;
  ratingReadOnly?: boolean;
  onRate: (rating: number) => void;
  canBookAgain: boolean;
  onBookAgain: () => void;
}

/** Footer for a COMPLETED rateable item: star rating + an optional "Book again" button. */
export const CardRatingFooter = ({
  rating,
  showRating = false,
  ratingReadOnly = false,
  onRate,
  canBookAgain,
  onBookAgain,
}: CardRatingFooterProps): JSX.Element => {
  const intl = useIntl();
  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onBookAgain();
    },
    [onBookAgain],
  );

  return (
    <div className="mt-3 flex items-center justify-between gap-2 border-t border-system-grey-light pt-3">
      {showRating ? <ActivityRating value={rating} onRate={onRate} readOnly={ratingReadOnly} /> : <span />}
      {canBookAgain && (
        <Button
          type="button"
          onClick={handleClick}
          data-testid="activity-card-book-again"
          className="h-auto shrink-0 gap-1.5 rounded-full px-[15px] py-2 text-[13px] font-semibold shadow-none"
        >
          <Repeat size={15} strokeWidth={2} />
          {intl.formatMessage({ id: 'activity.action.bookAgain' })}
        </Button>
      )}
    </div>
  );
};
